import React, { useContext, useEffect, useRef, useState } from 'react';
import GoogleAddressInput from './googleAddressSearch';
import FoundingWaitlist from './FoundingWaitlist';
import LiveArea from './LiveArea';
import ComingSoonFooter from './ComingSoonFooter'; // ← adjust path
import { StoreHeaderDataContext } from '../../context-api/StoreHeaderDataProvider';
import { DeliveryAddressDataContext } from '../../context-api/DeliveryAddressDataProvider';
import './ServiceCheckAddress.css';
import { localStorageKeys } from '../../global/constants';
import { useNativeNotificationFields } from '../../utils/reactNativeBridge';
import { getUrlParams } from '../../global/getUrl';
import FingerprintJS from '@fingerprintjs/fingerprintjs'; // same import as your StoreList page

const APP_STORE_URL = 'https://apps.apple.com/app/idXXXX'; // ← your link
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=XXXX'; // ← your link

/**
 * Routing rules
 *  success: false                                  → founding
 *  success: true && activeZipcode: true            → live
 *  success: true && isZipcodeAcceptingWaitlist     → founding
 *  success: true && activeZipcode: false           → founding
 */
const toStatus = (res = {}) => {
  const r = { ...(res.data || {}), ...res }; // fields may be top-level or inside .data
  return r.success === true && r.activeZipcode === true ? 'live' : 'founding';
};

const { FINGER_PRINT_USER } = localStorageKeys;

const randomId = () =>
  window.crypto?.randomUUID?.() || `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getDeviceId = async () => {
  // 1. Already generated on another page
  try {
    const saved = localStorage.getItem(FINGER_PRINT_USER);
    if (saved) return saved;
  } catch {}

  // 2. Generate it now, same as StoreList
  try {
    const fp = await FingerprintJS.load();
    const { visitorId } = await fp.get();
    if (visitorId) {
      try {
        localStorage.setItem(FINGER_PRINT_USER, visitorId);
      } catch {}
      return visitorId;
    }
  } catch (e) {
    console.warn('Fingerprint error:', e);
  }

  // 3. Last resort, so device_id is never null
  return randomId();
};

/* ─────────────────────────  Step 1: address  ───────────────────────── */
function AddressStep({ onResult }) {
  const {
    kitchensByZipcode = {},
    setZipcode,
    setIsDeliverable,
    onGetKitchenByZipcode,
  } = useContext(StoreHeaderDataContext) || {};
  const { googleAddress } = useContext(DeliveryAddressDataContext) || {};

  const { loading } = kitchensByZipcode;
  const [error, setError] = useState('');
  const submitted = useRef(false);
  const sawLoading = useRef(false);

  useEffect(() => {
    if (googleAddress?.zipcode) setError('');
  }, [googleAddress]);

  // Fire only after loading goes true → false, so old data never triggers a redirect
  useEffect(() => {
    if (!submitted.current) return;
    if (loading) {
      sawLoading.current = true;
      return;
    }
    if (!sawLoading.current) return;

    submitted.current = false;
    sawLoading.current = false;

    const status = toStatus(kitchensByZipcode);
    if (status === 'live') setIsDeliverable?.(true);
    onResult(status, googleAddress);
  }, [loading, kitchensByZipcode]);

  const onSubmit = (e) => {
    e.preventDefault();

    if (!googleAddress?.description) {
      setError('Start typing and pick your address from the list.');
      return;
    }
    if (!googleAddress.zipcode) {
      setError('That address has no ZIP code. Try your full street address.');
      return;
    }

    setError('');
    document.activeElement?.blur?.(); // close mobile keyboard
    submitted.current = true;
    sawLoading.current = false;
    setZipcode(googleAddress.zipcode);
    onGetKitchenByZipcode(googleAddress.zipcode, false);
  };

  return (
    <section className='ac'>
      <h1 className='ac-title'>Good food, brought closer to home.</h1>
      <p className='ac-lede'>
        Enter your address to see if we deliver to you today, or reserve a founding member spot
        before we arrive.
      </p>

      <form className='ac-panel' onSubmit={onSubmit} noValidate>
        <label className='ac-label'>
          <svg className='ac-labelIcon' viewBox='0 0 24 24' aria-hidden='true'>
            <path d='M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z' />
          </svg>
          Delivery address
        </label>

        <div className='ac-row'>
          <div className={`ac-inputWrap${error ? ' is-invalid' : ''}`}>
            <GoogleAddressInput styleAutoComplete={{ width: '100%', margin: 0 }} error={!!error} />
          </div>

          <button className='ac-btn' type='submit' disabled={loading}>
            {loading ? <span className='ac-spinner' aria-hidden='true' /> : null}
            {loading ? 'Checking…' : 'Check my address'}
          </button>
        </div>

        {error && (
          <p className='ac-error' role='alert'>
            {error}
          </p>
        )}
      </form>
    </section>
  );
}

/* ─────────────────────────  Flow controller  ───────────────────────── */
export default function ServiceCheckAddress() {
  const {
    clearZipcodeFromLocalStorage,
    setZipcode,
    setIsDeliverable,
    onWaitListFounderHandler,
    waitListResponse,
  } = useContext(StoreHeaderDataContext) || {};
  // const { googleAddress } = useContext(DeliveryAddressDataContext) || {};

  const urlParamsObject = getUrlParams();
  const urlInfluncer = urlParamsObject.promoterid;
  const urlStoreName = urlParamsObject?.storename;

  const Influncer = urlStoreName || urlInfluncer;

  const nativeNotificationFields = useNativeNotificationFields();

  const [step, setStep] = useState('address'); // 'address' | 'live' | 'founding'
  const [checked, setChecked] = useState({ address: '', zipcode: '' });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  // GoogleAddressInput clears googleAddress when it unmounts,
  // so save a copy before leaving the address step.
  const handleResult = (status, googleAddress) => {
    setChecked({
      address: googleAddress?.description || '',
      zipcode: googleAddress?.zipcode || '',
      Geolocation: googleAddress?.Geolocation, // ← keep it before the input clears it
    });
    setStep(status);
  };

  // Reset the saved ZIP so the next check calls the API again
  const backToAddress = () => {
    clearZipcodeFromLocalStorage?.();
    setZipcode?.('');
    setIsDeliverable?.(false);
    setChecked({ address: '', zipcode: '' });
    setStep('address');
  };

  // Waitlist API call

  const joinWaitlist = async (data) => {
    const WaitlistPayload = {
      device_id: await getDeviceId(),
      full_name: data?.name,
      managing_branch_name: 'humblefirstbranch1',
      phone_number: data?.phone,
      zipcode: data?.zipcode,
      joined_from: 'website',
      influencer_promoter_id: Influncer, // e.g. ?ref=Chicago%20Kabab%20House
      // promo_code: 'ME5566',
      notification_permission: false,
      device_type: 'web',
      Geolocation: checked.Geolocation, // ← saved copy, not googleAddress
    };

    console.log('WaitlistPayload', WaitlistPayload);
    const Resp = await onWaitListFounderHandler(WaitlistPayload);
    return Resp.data;
  };

  return (
    <div className='scp'>
      <main className='scp-main'>
        {step === 'address' && <AddressStep onResult={handleResult} />}

        {step === 'live' && (
          <LiveArea
            address={checked.address}
            onChangeAddress={backToAddress}
            appStoreUrl={APP_STORE_URL}
            playStoreUrl={PLAY_STORE_URL}
          />
        )}

        {step === 'founding' && (
          <FoundingWaitlist
            address={checked.address}
            zipcode={checked.zipcode}
            onChangeAddress={backToAddress}
            onJoin={joinWaitlist}
          />
        )}
      </main>

      <ComingSoonFooter />
    </div>
  );
}
